import React, { useState, useEffect } from 'react';
import './AdRotator.css';

const API_URL = process.env.REACT_APP_API_URL;

const AdRotator = ({ placement = 'sidebar' , floating = false}) => {
  const [currentAd, setCurrentAd] = useState(null);
  const [ads, setAds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch active ads
  useEffect(() => {
    fetchActiveAds();
  }, []);

  // Rotate ads every 10 seconds
  useEffect(() => {
    if (ads.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
    }, 10000); // Rotate every 10 seconds

    return () => clearInterval(interval);
  }, [ads.length]);

  // Update current ad when index changes
  useEffect(() => {
    if (ads.length > 0) {
      setCurrentAd(ads[currentIndex]);

      // Track impression
      if (ads[currentIndex]) {
        trackImpression(ads[currentIndex].id);
      }
    }
  }, [currentIndex, ads]);

  const fetchActiveAds = async () => {
    try {
      const response = await fetch(`${API_URL}/ads/active?placement=${placement}`);
      const data = await response.json();

      if (response.ok && data.ads.length > 0) {
        setAds(data.ads);
        setCurrentAd(data.ads[0]);
        trackImpression(data.ads[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch ads:', err);
    } finally {
      setLoading(false);
    }
  };

  const trackImpression = async (adId) => {
    try {
      await fetch(`${API_URL}/ads/impression/${adId}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Failed to track impression:', err);
    }
  };

  const handleAdClick = async () => {
    if (!currentAd) return;

    try {
      // Track click
      await fetch(`${API_URL}/ads/click/${currentAd.id}`, {
        method: 'POST'
      });

      // Open in new tab
      window.open(currentAd.targetUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to track click:', err);
      // Still open the link even if tracking fails
      window.open(currentAd.targetUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const adClassName = `ad-rotator ${floating ? 'floating' : ''}`;

  if (loading) {
    return (
      <div className={`${adClassName} loading`}>
        <div className="ad-placeholder">
          <span className="loading-spinner"></span>
        </div>
      </div>
    );
  }

  if (!currentAd) {
    return (
      <div className="ad-rotator empty">
        <div className="ad-placeholder">
          <p className="ad-label">Advertisement</p>
        </div>
      </div>
    );
  }

  return (
    <div className={adClassName/*"ad-rotator"*/}>
      <p className="ad-label">Advertisement</p>
      <div
        className="ad-container"
        onClick={handleAdClick}
        role="button"
        tabIndex={0}
        onKeyPress={(e) => e.key === 'Enter' && handleAdClick()}
      >
        <img
          src={`data:${currentAd.imageMimeType};base64,${currentAd.imageData}`}
          alt={`Advertisement from ${currentAd.companyName}`}
          className="ad-image"
        />
        <div className="ad-overlay">
          <span className="ad-company">{currentAd.companyName}</span>
        </div>
      </div>
      {ads.length > 1 && (
        <div className="ad-indicators">
          {ads.map((_, index) => (
            <span
              key={index}
              className={`indicator ${index === currentIndex ? 'active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdRotator;
